import apiClient from '../client';

const api = apiClient;

export interface PushSubscriptionKeys {
  p256dh: string;
  auth: string;
}

export interface PushSubscriptionRequest {
  endpoint: string;
  keys: PushSubscriptionKeys;
  userAgent?: string;
  deviceName?: string;
}

export interface PushSubscriptionResponse {
  id: string;
  endpointPreview: string;
  deviceType: 'DESKTOP' | 'MOBILE' | 'TABLET' | 'UNKNOWN';
  deviceName: string | null;
  subscriptionType: 'DASHBOARD' | 'PORTAL';
  active: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface PushStatus {
  subscribed: boolean;
  totalDashboardSubscriptions?: number;
}

/**
 * API para Web Push Notifications no Dashboard (profissionais).
 */
export const pushApi = {
  /**
   * Obtém a chave pública VAPID para criar subscriptions.
   */
  getVapidPublicKey: async (): Promise<{ publicKey: string }> => {
    const response = await api.get<{ publicKey: string }>('/v1/push/vapid-public-key');
    return response.data;
  },

  /**
   * Registra uma subscription.
   */
  subscribe: async (subscription: PushSubscriptionRequest): Promise<PushSubscriptionResponse> => {
    const response = await api.post<PushSubscriptionResponse>('/v1/push/subscribe', subscription);
    return response.data;
  },

  /**
   * Remove uma subscription.
   */
  unsubscribe: async (endpoint: string): Promise<void> => {
    await api.delete('/v1/push/unsubscribe', { data: { endpoint } });
  },

  /**
   * Lista subscriptions do usuário atual.
   */
  getMySubscriptions: async (): Promise<PushSubscriptionResponse[]> => {
    const response = await api.get<PushSubscriptionResponse[]>('/v1/push/subscriptions');
    return response.data;
  },

  /**
   * Verifica status de subscription.
   */
  getStatus: async (): Promise<PushStatus> => {
    const response = await api.get<PushStatus>('/v1/push/status');
    return response.data;
  },
};

/**
 * API para Web Push Notifications no Portal (pacientes).
 */
export const portalPushApi = {
  /**
   * Obtém a chave pública VAPID.
   */
  getVapidPublicKey: async (): Promise<{ publicKey: string }> => {
    const response = await api.get<{ publicKey: string }>('/v1/portal/push/vapid-public-key');
    return response.data;
  },

  /**
   * Registra subscription do paciente.
   */
  subscribe: async (subscription: PushSubscriptionRequest): Promise<PushSubscriptionResponse> => {
    const response = await api.post<PushSubscriptionResponse>('/v1/portal/push/subscribe', subscription);
    return response.data;
  },

  /**
   * Remove subscription do paciente.
   */
  unsubscribe: async (endpoint: string): Promise<void> => {
    await api.delete('/v1/portal/push/unsubscribe', { data: { endpoint } });
  },

  /**
   * Verifica status de subscription.
   */
  getStatus: async (): Promise<{ subscribed: boolean }> => {
    const response = await api.get<{ subscribed: boolean }>('/v1/portal/push/status');
    return response.data;
  },
};
